/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  User, EnvelopeSimple, Phone, Briefcase, MapPin, ArrowRight,
} from "@phosphor-icons/react";

/**
 * Read-only "Client Profile" overlay used by the Admin Clients tab and the
 * dedicated /clients page. Loads `/api/admin/clients/{id}` lazily on open
 * and renders:
 *   - master account fields (name / email / phone / status / joined)
 *   - every linked case with that client's per-case extended profile
 *     (case role, DOB, address, AML status, ID details, source of funds,
 *     mortgage status, notes).
 *
 * Solicitors edit individual fields via the case-scoped
 * `ClientDetailsDialog` (we link through to that case from here).
 */
const FIELDS = [
  { key: "company_name", label: "Company name", showIf: (p) => (p.entity_type || "individual") === "company" },
  { key: "company_registration_number", label: "Company reg. no.", showIf: (p) => (p.entity_type || "individual") === "company" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "dob", label: "Date of birth" },
  { key: "ni_number", label: "NI number" },
  { key: "marital_status", label: "Marital status" },
  { key: "id_type", label: "ID type" },
  { key: "id_number", label: "ID number" },
  { key: "id_expiry", label: "ID expiry" },
  { key: "aml_status", label: "AML status" },
  { key: "source_of_funds", label: "Source of funds" },
  { key: "mortgage_status", label: "Mortgage status" },
  { key: "notes", label: "Notes" },
];

const AML_PILL = {
  "Cleared": "bg-emerald-50 text-emerald-700",
  "Failed": "bg-rose-50 text-rose-700",
  "In progress": "bg-amber-50 text-amber-700",
  "Not started": "bg-slate-100 text-slate-600",
};

export default function ClientProfileDialog({ open, onOpenChange, clientId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    (async () => {
      try {
        const { data } = await API.get(`/admin/clients/${clientId}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="client-profile-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            {data ? data.name : "Client profile"}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="client-profile-loading">Loading…</div>
        )}

        {!loading && !data && (
          <div className="py-10 text-center text-sm text-slate-400">Client not found.</div>
        )}

        {!loading && data && (
          <div className="space-y-5 pb-2">
            {/* Master profile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="client-master">
              <Row icon={<EnvelopeSimple weight="bold" className="w-3.5 h-3.5" />} label="Email" value={data.email} />
              <Row icon={<Phone weight="bold" className="w-3.5 h-3.5" />} label="Phone" value={data.phone || "—"} />
              <Row icon={<User weight="bold" className="w-3.5 h-3.5" />} label="Status" value={data.is_active === false ? "Deactivated" : "Active"} />
              <Row icon={<Briefcase weight="bold" className="w-3.5 h-3.5" />} label="Joined" value={data.created_at ? new Date(data.created_at).toLocaleDateString("en-GB") : "—"} />
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Linked cases ({data.cases_count})
              </h3>
            </div>

            {data.cases.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No cases linked to this client yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="client-cases-list">
                {data.cases.map((c) => {
                  const profile = c.client_profile || {};
                  const captured = FIELDS.filter((f) => profile[f.key] && (!f.showIf || f.showIf(profile)));
                  return (
                    <li key={c.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`client-case-${c.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <button
                            onClick={() => { onOpenChange(false); navigate(`/cases/${c.id}`); }}
                            className="text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors text-left flex items-center gap-1"
                            data-testid={`client-case-link-${c.id}`}
                          >
                            {c.title}
                            <ArrowRight weight="bold" className="w-3 h-3" />
                          </button>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin weight="fill" className="w-3 h-3 text-slate-400" />
                            {c.property_address || "—"}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            c.status === "completed" ? "bg-emerald-50 text-emerald-700"
                              : c.status === "archived" ? "bg-slate-100 text-slate-500"
                              : "bg-blue-50 text-blue-700"
                          }`}>{c.status}</span>
                          {profile.case_role && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                              {profile.case_role}
                            </span>
                          )}
                        </div>
                      </div>
                      {captured.length === 0 ? (
                        <p className="text-[11px] text-slate-400 italic">No extended details captured for this case yet.</p>
                      ) : (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-2 border-t border-slate-100">
                          {captured.map((f) => (
                            <div key={f.key} className="grid grid-cols-3 gap-2 text-[11px]">
                              <dt className="text-slate-500 col-span-1">{f.label}</dt>
                              <dd className="text-slate-900 col-span-2 break-words">
                                {f.key === "aml_status" ? (
                                  <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full ${AML_PILL[profile[f.key]] || "bg-slate-100 text-slate-500"}`}>
                                    {profile[f.key]}
                                  </span>
                                ) : profile[f.key]}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-400">{icon}</span>
      <span className="text-slate-500 w-16 shrink-0">{label}</span>
      <span className="text-slate-900 font-medium truncate">{value}</span>
    </div>
  );
}
